import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV } from "@/config/site";

export const LogoLockup = ({ testId = "header-logo" }) => (
  <Link to="/" className="flex items-center gap-3 shrink-0" aria-label={`${COMPANY.name} home`} data-testid={testId}>
    <img src={COMPANY.mark} alt="" width="48" height="34" className="h-8 w-auto md:h-9" />
    <span className="leading-none">
      <span className="font-display block text-[1.15rem] font-semibold tracking-tight">Suvirika</span>
      <span className="font-mono-label block text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ink-2)] mt-1">Retail Pvt Ltd</span>
    </span>
  </Link>
);

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="fixed inset-0 z-40 bg-[var(--paper)] lg:hidden flex flex-col pt-28 pb-10 container-x"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    data-testid="mobile-menu"
    data-lenis-prevent
  >
    <nav aria-label="Mobile" className="flex flex-col">
      {NAV.map((n, i) => (
        <motion.div key={n.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }}>
          <NavLink to={n.to} end onClick={onClose} className="font-display block border-b hairline py-4 text-3xl" data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}>
            {n.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <Link to="/partner-with-us" onClick={onClose} className="btn-ink mt-10 self-start" data-testid="mobile-partner-cta">
      Partner With Us <ArrowUpRight size={18} />
    </Link>
  </motion.div>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          scrolled || open ? "bg-paper/85 backdrop-blur-xl hairline" : "border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="container-x flex h-20 items-center justify-between gap-6">
          <LogoLockup />
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end
                className={({ isActive }) => `link-underline text-[0.92rem] font-medium pb-1 ${isActive ? "text-[var(--ink)] [background-size:100%_1px]" : "text-[var(--ink-2)]"}`}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/partner-with-us" className="btn-ink !py-2.5 !px-5 !text-sm hidden sm:inline-flex" data-testid="header-partner-cta">
              Partner With Us
            </Link>
            <button
              type="button"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border hairline"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}
